// InstallBanner — small bottom-sheet prompt for mobile browsers to install
// Rostr as a PWA. Shown only when: (a) mobile viewport, (b) NOT already
// running as a PWA, (c) not dismissed within the last 7 days.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Share, Plus, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { isRunningAsApp } from "@/components/AppOnboarding";

const DISMISS_KEY = "rostr.install_banner_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 dagen

function shouldShow() {
  if (typeof window === "undefined") return false;
  if (isRunningAsApp()) return false;
  const isMobile = window.innerWidth <= 820;
  if (!isMobile) return false;
  try {
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_MS) return false;
  } catch { /* ignore */ }
  return true;
}

export default function InstallBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  // Detect iOS (share-sheet based install) vs Android/desktop (beforeinstallprompt)
  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    // Small delay so it doesn't compete with page-load animations
    const timer = setTimeout(() => setVisible(shouldShow()), 1400);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="fixed left-4 right-4 z-[80] rounded-3xl overflow-hidden"
          style={{
            bottom: "max(env(safe-area-inset-bottom, 0px), 16px)",
            background: "linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(var(--primary-soft)) 100%)",
            border: "1px solid hsl(var(--border))",
            boxShadow: "0 24px 60px -20px rgb(0 0 0 / 0.28), 0 6px 16px -6px rgb(0 0 0 / 0.12)",
          }}
          data-testid="install-banner"
        >
          <div className="p-4 flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-2xl grid place-items-center shrink-0 text-white"
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
                boxShadow: "0 8px 20px -6px hsl(var(--primary) / 0.5)",
              }}
            >
              <Smartphone size={18} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                {t("install_banner_title")}
              </div>
              <div className="mt-1 text-[12px] text-[hsl(var(--text-muted))] leading-snug">
                {isIOS ? (
                  <>
                    {t("install_banner_ios_pre")}
                    <span className="inline-flex items-center align-middle mx-1">
                      <Share size={11} strokeWidth={2.2} />
                    </span>
                    {t("install_banner_ios_mid")}
                    <span className="mx-1 font-semibold text-[hsl(var(--text-strong))]">"{t("install_banner_ios_add")}"</span>
                  </>
                ) : (
                  t("install_banner_desc")
                )}
              </div>
            </div>
            <button
              onClick={dismiss}
              data-testid="install-banner-dismiss"
              className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] shrink-0"
              aria-label={t("close")}
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
